import { EMAIL_NOT_VERIFIED } from '@/lib/email-verification';
import { PAT_SCOPES } from '@/lib/personal-access-tokens';

const CONSENT_ERRORS: Record<string, string> = {
  invalid_client:
    'The app that sent you here is not registered or its registration could not be verified.',
  invalid_redirect_uri: 'The app asked to return to an address it has not registered.',
  invalid_request: 'The app sent an incomplete or malformed request.',
  temporarily_unavailable: 'Too many connection attempts. Wait a few minutes and try again.',
};

export function consentErrorMessage(code: string): string {
  return CONSENT_ERRORS[code] ?? 'The app sent a request that could not be processed.';
}

export function defaultSelectedScopes(requested: string[]): string[] {
  return requested.filter((value) => {
    const info = PAT_SCOPES.find((s) => s.value === value);
    return info !== undefined && !info.mutation;
  });
}

// The backend's redirect URI rules (RedirectUris), applied again before the consent page follows a redirect.
const UNDOTTED_APP_SCHEMES = new Set(['cursor']);
const NOT_APP_SCHEMES = new Set([
  'http',
  'https',
  'javascript',
  'data',
  'file',
  'vbscript',
  'about',
  'blob',
  'filesystem',
  'ftp',
  'ws',
  'wss',
  'mailto',
  'tel',
  'sms',
  'intent',
  'chrome',
  'chrome-extension',
  'moz-extension',
  'view-source',
  'jar',
  'googlechrome',
  'googlechromes',
  'googlechrome-x-callback',
  'firefox',
  'firefox-focus',
  'microsoft-edge',
  'microsoft-edge-http',
  'microsoft-edge-https',
  'x-safari-http',
  'x-safari-https',
  'opera-http',
  'opera-https',
  'brave',
  'duckduckgo',
]);
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);
const APP_AUTHORITY = /^[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*(?::([0-9]{1,5}))?$/;

/**
 * Whether the consent page may follow a redirect: HTTPS, loopback HTTP, or a link into a native app's own scheme
 * under the same rules as the backend. Without the backend's length limit, because the code and state lengthen it.
 */
export function isFollowableRedirect(url: string): boolean {
  // Whitespace, control characters and backslashes are refused before parsing, because the URL parser silently
  // drops or rewrites them; a redirect never has a fragment.
  if (
    /[\s\\#]/.test(url) ||
    [...url].some((c) => c.charCodeAt(0) < 0x20 || c.charCodeAt(0) === 0x7f)
  ) {
    return false;
  }
  const match = /^([A-Za-z][A-Za-z0-9+.-]*):(.*)$/.exec(url);
  if (!match) return false;
  const scheme = match[1]!.toLowerCase();
  const rest = match[2]!;
  if (scheme === 'https' || scheme === 'http') {
    try {
      const parsed = new URL(url);
      if (parsed.username || parsed.password) return false;
      return scheme === 'https' ? parsed.hostname !== '' : LOOPBACK_HOSTS.has(parsed.hostname);
    } catch {
      return false;
    }
  }
  if (NOT_APP_SCHEMES.has(scheme)) return false;
  if (!scheme.includes('.') && !UNDOTTED_APP_SCHEMES.has(scheme)) return false;
  if (!rest.startsWith('//')) return rest.startsWith('/');
  const authority = rest.slice(2).split(/[/?]/, 1)[0]!;
  if (authority === '') return rest.slice(2).startsWith('/');
  const port = APP_AUTHORITY.exec(authority);
  if (!port) return false;
  return port[1] === undefined || (Number(port[1]) >= 1 && Number(port[1]) <= 65535);
}

export interface OAuthApiError {
  message: string;
  status?: number;
  notFound?: boolean;
  emailNotVerified?: boolean;
}

export function oauthApiError(e: unknown, action: string): OAuthApiError {
  const { response } = (e ?? {}) as { response?: { status?: number; data?: unknown } };
  if (!response) {
    return {
      message: `Could not ${action}: the server is unreachable. Check your connection and try again.`,
    };
  }
  const status = response.status;
  const code = (
    response.data && typeof response.data === 'object'
      ? (response.data as { code?: unknown }).code
      : undefined
  ) as string | undefined;
  if (status === 404) {
    return { message: `Could not ${action}: it no longer exists.`, status, notFound: true };
  }
  if (status === 403 && code === EMAIL_NOT_VERIFIED) {
    return {
      message: 'Verify your email address before connecting apps.',
      status,
      emailNotVerified: true,
    };
  }
  if (status === 403) {
    return {
      message:
        'This session cannot connect apps. Sign in again, completing multi-factor authentication if enabled.',
      status,
    };
  }
  if (status === 400 && code === 'INVALID_OAUTH_CONSENT') {
    return { message: 'Select at least one of the requested permissions.', status };
  }
  return { message: `Could not ${action} (HTTP ${status ?? 'error'}). Try again later.`, status };
}
